// CRAP is inflated only when the CI fallow coverage step times out (0% scoring, see
// .github/workflows/fallow.yml); these branchy fns are fully unit-tested. Drop when fixed.
// fallow-ignore-file complexity
// @plasser/ui — pure search logic (no React, no I/O → trivially unit-testable).
// AND-token full-text over name + subtitle + configured searchable attributes,
// plus pill filtering and match-highlighting.

import type { EntityRecord, EntityTypeConfig, SearchQuery, SearchResult } from "./types";

/** Lowercase, whitespace-split, drop empties. `"  Schmidt  Indien "` → `["schmidt","indien"]`. */
export function tokenize(text: string): string[] {
  return (text ?? "").trim().toLowerCase().split(/\s+/).filter(Boolean);
}

/** All searchable strings for one entity (name + subtitle + configured attributes). */
export function entitySearchableValues(entity: EntityRecord, config: EntityTypeConfig): string[] {
  const values: string[] = [entity.name ?? ""];
  if (entity.subtitle) values.push(String(entity.subtitle));
  for (const key of config.searchableAttributes) {
    const v = entity.attributes?.[key];
    if (v != null && v !== "") values.push(String(v));
  }
  return values;
}

/**
 * AND-token match: EVERY token must appear in at least one searchable value.
 * Empty token list matches everything. This is what lets "Mechanical Indien"
 * find a group whose name is "Mechanical" and whose standort attribute is "Indien".
 */
export function matchesTokens(
  entity: EntityRecord,
  config: EntityTypeConfig,
  tokens: string[],
): boolean {
  if (tokens.length === 0) return true;
  const haystack = entitySearchableValues(entity, config).join(" ").toLowerCase();
  return tokens.every((t) => haystack.includes(t));
}

/** Pill filter: for each active key the attribute value must be one of the selected values (OR within a key, AND across keys). */
export function matchesFilters(
  entity: EntityRecord,
  filters: Record<string, string[]> | undefined,
): boolean {
  if (!filters) return true;
  for (const [key, values] of Object.entries(filters)) {
    if (!values || values.length === 0) continue;
    const v = entity.attributes?.[key];
    if (v == null || !values.includes(String(v))) return false;
  }
  return true;
}

/**
 * Client-side search over an in-memory set (used by the MockSearchProvider and
 * as a fallback). Server providers replicate this contract in SQL.
 */
export function localSearch(
  config: EntityTypeConfig,
  entities: EntityRecord[],
  query: SearchQuery,
): SearchResult {
  const tokens = tokenize(query.text);
  const filtered = entities.filter(
    (e) => matchesTokens(e, config, tokens) && matchesFilters(e, query.filters),
  );
  const total = filtered.length;
  const offset = Math.max(0, query.offset ?? 0);
  const limit = query.limit ?? total;
  const items = filtered.slice(offset, offset + limit);
  return { items, total };
}

/** Distinct attribute values present in a result set (for deriving filter-pill options). */
export function deriveFilterOptions(
  entities: EntityRecord[],
  key: string,
): { value: string; label: string }[] {
  const seen = new Set<string>();
  for (const e of entities) {
    const v = e.attributes?.[key];
    if (v != null && v !== "") seen.add(String(v));
  }
  return [...seen].sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v }));
}

export interface HighlightSegment {
  text: string;
  match: boolean;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Split `text` into matched/unmatched segments for the given tokens, so the UI
 * can wrap matches in <mark>. Case-insensitive, longest-token-first to avoid a
 * short token swallowing a longer overlapping one.
 */
export function highlightMatches(text: string, tokens: string[]): HighlightSegment[] {
  if (!text) return [{ text: "", match: false }];
  const escaped = tokens
    .filter(Boolean)
    .slice()
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp);
  if (escaped.length === 0) return [{ text, match: false }];

  const re = new RegExp(`(${escaped.join("|")})`, "gi");
  const segments: HighlightSegment[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) segments.push({ text: text.slice(last, m.index), match: false });
    segments.push({ text: m[0], match: true });
    last = m.index + m[0].length;
    if (m.index === re.lastIndex) re.lastIndex++; // guard zero-length matches
  }
  if (last < text.length) segments.push({ text: text.slice(last), match: false });
  return segments;
}
